// Memoria Gobernada — display del grafo del cerebro soberano.
// Regla de oro: pinta EXACTAMENTE lo que el cerebro devuelve. Campo vacío = dato.
// Cumple docs/MEMORY_FRONTEND_DISPLAY: color por clasif, tamaño ∝ log(occurrences),
// gobernanza vetoed≠normal, distinct+total, corrects resaltado, redacción sin fuga,
// procedencia completa, edición append-only sin borrar, access_level del backend.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Brain, Shield, Lock, CheckCircle2, Ban, HelpCircle, RefreshCw, FileText,
  MessageCircle, User, Gavel, AlertCircle, Network, Upload, Sparkles,
} from 'lucide-react';
import {
  memoryApi, type GraphResponse, type MemoryNode, type Clasif, type GovStatus,
} from '../api';
import { MemoryGraphCanvas } from './MemoryGraphCanvas';
import { MobileMemoryGraph } from './MobileMemoryGraph';
import { useIsMobile } from '@/components/ui/useIsMobile';
import { Modal } from '@/components/ui/Modal';
import { usePaginated, Pagination } from '@/components/ui/Pagination';

const CLASIF_COLOR: Record<Clasif, string> = {
  publica: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  interna: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  restringida: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  confidencial: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
};
const CLASIF_DOT: Record<Clasif, string> = {
  publica: '🟢', interna: '🔵', restringida: '🟠', confidencial: '🔴',
};

// Tope de nodos a RENDERIZAR en el grafo (rendimiento + legibilidad).
const GRAPH_MAX = 140;

// Acota el grafo a las memorias más reforzadas + su procedencia inmediata.
// Devuelve el subgrafo y cuántas memorias se muestran vs el total (para el badge).
function capGraph(data: GraphResponse, max: number): { graph: GraphResponse; capped: { shown: number; total: number } | null } {
  if (data.nodes.length <= max) return { graph: data, capped: null };
  const mems = data.nodes.filter((n) => n.type === 'memory')
    .sort((a, b) => (b.occurrences ?? 1) - (a.occurrences ?? 1));
  const adj = new Map<string, string[]>();
  for (const e of data.edges) {
    (adj.get(e.source) ?? adj.set(e.source, []).get(e.source)!).push(e.target);
    (adj.get(e.target) ?? adj.set(e.target, []).get(e.target)!).push(e.source);
  }
  const kept = new Set<string>();
  for (const m of mems) {
    if (kept.size >= max) break;
    kept.add(m.id);
    for (const nb of adj.get(m.id) ?? []) if (kept.size < max) kept.add(nb);
  }
  const nodes = data.nodes.filter((n) => kept.has(n.id));
  const edges = data.edges.filter((e) => kept.has(e.source) && kept.has(e.target));
  const shown = nodes.filter((n) => n.type === 'memory').length;
  return { graph: { meta: data.meta, nodes, edges }, capped: { shown, total: mems.length } };
}

function GovBadge({ status, veto }: { status?: GovStatus; veto?: string | null }) {
  if (status === 'approved')
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" title="aprobada por los 6 gates"><CheckCircle2 size={13} /> aprobada</span>;
  if (status === 'vetoed')
    return <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title={`vetada por ${veto ?? '—'}`}><Ban size={13} /> vetada{veto ? ` · ${veto}` : ''}</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="sin veredicto en el rastro"><HelpCircle size={13} /> sin veredicto</span>;
}

export function MemoryGraphPage() {
  const [data, setData] = useState<GraphResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MemoryNode | null>(null);
  const [view, setView] = useState<'graph' | 'list'>('graph');
  const [correlacion, setCorrelacion] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  const isMobile = useIsMobile();
  const [immersive, setImmersive] = useState(false);

  async function load(sim = correlacion) {
    setLoading(true);
    setError(null);
    try {
      setData(await memoryApi.getGraph({ redaction: 'count', similarity: sim }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function toggleCorrelacion() {
    const next = !correlacion;
    setCorrelacion(next);
    load(next);
  }

  const memories = useMemo(
    () => (data?.nodes ?? []).filter((n) => n.type === 'memory'),
    [data],
  );

  // El grafo se vuelve inmanejable (y deja de ser estudiable) con cientos de
  // nodos. Acotamos el RENDER a las memorias más reforzadas + su procedencia,
  // sin silenciarlo: las stats muestran los totales reales y el badge dice
  // "mostrando N de M". La vista de correlación ya viene acotada del backend.
  const graphData = useMemo(() => data ? capGraph(data, GRAPH_MAX) : null, [data]);
  const maxOcc = useMemo(
    () => Math.max(1, ...memories.map((m) => m.occurrences ?? 1)),
    [memories],
  );
  // Lista paginada por default (datos masivos: ucognet ~976 memorias).
  const pag = usePaginated(memories);

  if (loading && !data) {
    return <div className="p-6 text-sm text-muted-foreground">Cargando memoria…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
          <AlertCircle size={15} /> {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const c = data.meta.counts;
  const gov = c.by_governance || {};
  const redacted = data.meta.redacted || {};

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary"><Brain size={22} /></div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Memoria gobernada</h1>
            <p className="text-sm text-muted-foreground">
              Lo que el agente soberano sabe, con procedencia y gobernanza — auditable y corregible.
            </p>
          </div>
        </div>
        <button onClick={() => load()} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground disabled:opacity-50 hover:bg-muted">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      {/* Resumen honesto: distinct + total, gobernanza, clearance del visor */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Memorias distintas" value={c.distinct_memories} sub={`${c.memories} episodios`} />
        <Stat label="Gobernanza"
          value={`${gov.approved ?? 0}✅ ${gov.vetoed ?? 0}⛔`}
          sub={`${gov.unknown ?? 0} sin veredicto`} />
        <Stat label="Nodos · aristas" value={`${c.nodes} · ${c.edges}`} />
        <Stat label="Tu nivel de acceso"
          value={<span className="inline-flex items-center gap-1"><Shield size={14} /> {data.meta.access_level}</span>}
          sub="lo fija el servidor" />
      </div>

      {/* Redacción — transparencia sin fuga */}
      {Object.keys(redacted).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(redacted).map(([lvl, v]) => (
            <span key={lvl} className="inline-flex items-center gap-1.5 text-xs rounded-lg border border-border bg-muted/50 px-2.5 py-1 text-muted-foreground">
              <Lock size={12} /> {typeof v === 'number' ? `${v} ${lvl} ocultos` : `hay ${lvl} que no puedes ver`}
            </span>
          ))}
        </div>
      )}

      {/* Barra de herramientas */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs">
          {(['graph', 'list'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 font-medium ${view === v ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`}>
              {v === 'graph' ? 'Grafo' : 'Lista'}
            </button>
          ))}
        </div>
        {view === 'graph' && (
          <button onClick={toggleCorrelacion}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${
              correlacion ? 'bg-pink-500/15 border-pink-500/40 text-pink-600 dark:text-pink-400' : 'border-border text-foreground hover:bg-muted'}`}>
            <Network size={13} /> Correlación semántica
          </button>
        )}
        <button onClick={() => setIngestOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted ml-auto">
          <Upload size={13} /> Subir documento
        </button>
      </div>

      {/* Stats de correlación */}
      {correlacion && data.meta.similarity && (
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-lg border border-border bg-muted/50 px-2.5 py-1 text-muted-foreground">
            {data.meta.similarity.clusters} clusters · {data.meta.similarity.similar_edges} correlaciones (≥ {data.meta.similarity.threshold})
          </span>
          {data.meta.similarity.capped_from > 0 && (
            <span className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-amber-600 dark:text-amber-400">
              usando las {data.meta.similarity.considered} más reforzadas de {data.meta.similarity.capped_from}
            </span>
          )}
        </div>
      )}

      {memories.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-border rounded-xl p-6 text-center">
          Esta organización aún no tiene memoria visible en tu nivel de acceso.
        </div>
      ) : view === 'graph' && graphData ? (
        <>
          {graphData.capped && (
            <div className="inline-flex items-center gap-1.5 text-[11px] rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-amber-600 dark:text-amber-400">
              <Network size={12} /> Mostrando las {graphData.capped.shown} memorias más reforzadas de {graphData.capped.total} — usa Correlación o Lista para explorar el resto
            </div>
          )}
          {isMobile ? (
            // Móvil: el grafo con mouse no funciona → tarjeta que abre el modo inmersivo táctil.
            <button onClick={() => setImmersive(true)}
              className="w-full overflow-hidden rounded-2xl border border-slate-800/60 text-left active:scale-[0.99] transition"
              style={{ background: 'radial-gradient(120% 90% at 50% 25%, #182238 0%, #0b1220 60%, #060912 100%)', boxShadow: '0 20px 60px -20px rgba(0,0,0,0.6)' }}>
              <div className="p-6 min-h-[220px] flex flex-col justify-between">
                <div className="flex flex-wrap gap-1.5">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <span key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: ['#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#0ea5e9', '#f5c518'][i], boxShadow: `0 0 8px ${['#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#0ea5e9', '#f5c518'][i]}` }} />
                  ))}
                </div>
                <div>
                  <p className="text-slate-100 font-semibold">Explorar la memoria en modo inmersivo</p>
                  <p className="text-slate-400 text-xs mt-1">Pantalla completa · toca, arrastra, pellizca e inclina el teléfono para navegar el conocimiento del agente.</p>
                  <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-slate-100 rounded-full px-3 py-1.5 bg-white/10 border border-white/15">
                    <Sparkles size={13} /> Abrir
                  </span>
                </div>
              </div>
            </button>
          ) : (
            <>
              <MemoryGraphCanvas data={graphData.graph} onSelect={setSelected}
                colorBy={correlacion ? 'cluster' : 'clasif'} />
              <p className="text-[11px] text-muted-foreground">
                Arrastra para mover · rueda para zoom · clic en una memoria para su procedencia.
                {correlacion && ' Color por cluster; líneas rosas = similitud semántica.'}
              </p>
            </>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {pag.pageItems.map((m) => {
            const cl = (m.clasif ?? 'publica') as Clasif;
            const weight = Math.round(4 + 22 * (Math.log((m.occurrences ?? 1) + 1) / Math.log(maxOcc + 1)));
            const vetoed = m.governance?.status === 'vetoed';
            return (
              <button key={m.id} onClick={() => setSelected(m)}
                className={`w-full text-left rounded-xl border px-4 py-3 transition hover:bg-muted/40 ${
                  vetoed ? 'border-red-500/50 border-dashed' : 'border-border'} bg-card`}>
                <div className="flex items-center gap-3">
                  {/* peso ∝ log(occurrences) */}
                  <span className="shrink-0 rounded-full bg-primary/70"
                    style={{ width: weight, height: weight }} title={`reforzada ${m.occurrences ?? 1}×`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${CLASIF_COLOR[cl]}`}>
                        {CLASIF_DOT[cl]} {cl}
                      </span>
                      <GovBadge status={m.governance?.status} veto={m.governance?.veto_module} />
                      <span className="text-[11px] text-muted-foreground">
                        {m.source_type ?? 'unknown'} · reforzada {m.occurrences ?? 1}×
                      </span>
                    </div>
                    <p className="text-sm text-foreground mt-0.5 truncate">{m.label || '—'}</p>
                  </div>
                </div>
              </button>
            );
          })}
          <Pagination page={pag.page} pageCount={pag.pageCount} total={pag.total}
            from={pag.from} to={pag.to} onPage={pag.setPage} label="memorias" />
        </div>
      )}

      {/* Integridad */}
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Lock size={11} /> Integridad · content_sha256 <code>{data.meta.content_sha256?.slice(0, 16)}…</code>
      </p>

      {selected && <ProvenanceDrawer node={selected} onClose={() => setSelected(null)} onEdited={() => load()} />}
      {ingestOpen && <IngestModal onClose={() => setIngestOpen(false)} onIngested={() => load()} />}
      {immersive && graphData && (
        <MobileMemoryGraph data={graphData.graph} onClose={() => setImmersive(false)}
          colorBy={correlacion ? 'cluster' : 'clasif'}
          correlacion={correlacion} onToggleCorrelacion={toggleCorrelacion} />
      )}
    </div>
  );
}

// Subir un documento a la memoria (texto ya extraído; el cerebro lo chunkea y gobierna)
function IngestModal({ onClose, onIngested }: { onClose: () => void; onIngested: () => void }) {
  const [docRef, setDocRef] = useState('');
  const [text, setText] = useState('');
  const [clasif, setClasif] = useState<Clasif>('interna');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit() {
    if (!docRef.trim() || !text.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await memoryApi.ingest({ doc_ref: docRef.trim(), text: text.trim(), clasif });
      const vetoed = r.per_chunk.filter((c) => !c.applied).length;
      setResult({
        ok: true,
        text: `${r.applied}/${r.chunks} chunks gobernados y firmados${vetoed ? ` · ${vetoed} vetados` : ''}`,
      });
      setText(''); setDocRef('');
      setTimeout(onIngested, 900);
    } catch (e) {
      const err = e as { status?: number; message: string };
      setResult({ ok: false, text: err.status === 403 ? 'No tienes clearance para escribir a este nivel' : err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} size="lg"
      title={<span className="flex items-center gap-2"><Upload size={16} /> Subir documento a la memoria</span>}
      footer={
        <button onClick={submit} disabled={busy || !docRef.trim() || !text.trim()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 hover:opacity-90">
          <Upload size={15} /> {busy ? 'Ingiriendo…' : 'Alimentar memoria'}
        </button>
      }>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Pega el texto del documento (reglamento, acuerdo…). El cerebro lo divide en fragmentos, y cada uno pasa por los 6 gates y queda como memoria firmada con procedencia.
        </p>
        <input value={docRef} onChange={(e) => setDocRef(e.target.value)}
          placeholder="nombre_del_documento.pdf"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
          placeholder="Pega aquí el texto del documento…"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground resize-none" />
        <div className="flex items-center gap-3">
          <label className="text-xs text-muted-foreground">Clasificación</label>
          <select value={clasif} onChange={(e) => setClasif(e.target.value as Clasif)}
            className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-foreground">
            <option value="publica">pública</option>
            <option value="interna">interna</option>
            <option value="restringida">restringida</option>
            <option value="confidencial">confidencial</option>
          </select>
        </div>
        {result && (
          <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${result.ok ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-destructive/10 text-destructive'}`}>
            {result.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />} {result.text}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

const SOURCE_ICON: Record<string, ReactNode> = {
  document: <FileText size={13} />,
  conversation: <MessageCircle size={13} />,
  correction: <Gavel size={13} />,
};

// Panel de procedencia + edición gobernada (append-only)
function ProvenanceDrawer({ node, onClose, onEdited }: {
  node: MemoryNode; onClose: () => void; onEdited: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [annotation, setAnnotation] = useState('');
  const [kind, setKind] = useState<'correct' | 'augment' | 'redact_request'>('correct');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit() {
    if (!annotation.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await memoryApi.annotate({
        clasif: (node.clasif ?? 'interna') as Clasif,
        annotation: annotation.trim(),
        target_decision_id: node.decision_id,
        edit_kind: kind,
      });
      if (r.applied) {
        setResult({ ok: true, text: `Corrección firmada ${r.decision_id?.slice(0, 8)}` });
        setAnnotation('');
        setTimeout(onEdited, 800); // re-fetch: aparece el nodo correction
      } else {
        setResult({ ok: false, text: `Rechazada por la reja${r.veto_module ? ` (${r.veto_module})` : ''} — la gobernanza funcionando` });
      }
    } catch (e) {
      const err = e as { status?: number; message: string };
      setResult({ ok: false, text: err.status === 403 ? 'No tienes clearance para escribir a este nivel' : err.message });
    } finally {
      setBusy(false);
    }
  }

  const cl = (node.clasif ?? 'publica') as Clasif;
  return (
    <Modal open onClose={onClose} size="md" title={node.label || 'Memoria'}>
      <div className="space-y-4">
        {/* Procedencia completa — el valor del producto */}
        <dl className="text-sm space-y-2">
          <Row k="Clasificación" v={<span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border ${CLASIF_COLOR[cl]}`}>{CLASIF_DOT[cl]} {cl}</span>} />
          <Row k="Gobernanza" v={<GovBadge status={node.governance?.status} veto={node.governance?.veto_module} />} />
          <Row k="Alimentada por" v={<span className="inline-flex items-center gap-1"><User size={12} /> {node.fed_by || '—'}</span>} />
          <Row k="Origen" v={<span className="inline-flex items-center gap-1">{SOURCE_ICON[node.source_type ?? ''] ?? null} {node.source_type ?? '—'}{node.doc_ref ? `: ${node.doc_ref}` : ''}</span>} />
          <Row k="Ingresada" v={node.ingested_at || '—'} />
          <Row k="Reforzada" v={`${node.occurrences ?? 1}× ${node.last_seen ? `(último ${node.last_seen})` : ''}`} />
          <Row k="Decisión firmada" v={<code className="text-xs">🔏 {node.decision_id || '—'}</code>} />
        </dl>

        {/* Edición gobernada — SOLO si el cerebro dice may_annotate. Sin borrar/editar en sitio. */}
        {node.may_annotate && !editing && (
          <button onClick={() => setEditing(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
            <Gavel size={15} /> Proponer corrección
          </button>
        )}

        {editing && (
          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">
              La edición es <strong>append-only</strong>: no borra nada; añade un asiento de ajuste firmado que pasa por los 6 gates.
            </p>
            <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
              <option value="correct">Corregir (enmienda)</option>
              <option value="augment">Ampliar (añade contexto)</option>
              <option value="redact_request">Solicitar redacción (deja constancia)</option>
            </select>
            <textarea value={annotation} onChange={(e) => setAnnotation(e.target.value)} rows={3}
              placeholder="Describe la corrección…"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground resize-none" />
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy || !annotation.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 hover:opacity-90">
                {busy ? 'Firmando…' : 'Enviar corrección'}
              </button>
              <button onClick={() => setEditing(false)} className="rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-muted">Cancelar</button>
            </div>
          </div>
        )}

        {result && (
          <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${result.ok ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
            {result.ok ? <CheckCircle2 size={15} /> : <Ban size={15} />} {result.text}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-muted-foreground shrink-0 pt-0.5">{k}</dt>
      <dd className="text-sm text-foreground text-right">{v}</dd>
    </div>
  );
}
